import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Brain, Cpu, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

const Home = () => {
  return (
    <div className="bg-darkGrey text-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden tech-bg min-h-[80vh] flex items-center">
        {/* Animated particles/grid */}
        <div className="absolute inset-0 z-0 opacity-30">
          <div className="absolute w-full h-full bg-[radial-gradient(circle_at_center,_rgba(138,43,226,0.2)_0,_rgba(0,162,255,0.1)_25%,_transparent_100%)]"></div>
        </div>

        {/* Floating elements */}
        <motion.div
          className="absolute top-20 right-20 w-32 h-32 rounded-full bg-deepPurple/20 blur-xl"
          animate={{
            y: [0, 15, 0],
            opacity: [0.5, 0.8, 0.5]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        <motion.div
          className="absolute bottom-40 left-20 w-24 h-24 rounded-full bg-electricBlue/20 blur-xl"
          animate={{
            y: [0, -20, 0],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />

        <div className="w-full min-w-0 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              <span className="text-white">Big possibilities.</span><br/>
              <span className="text-electricBlue">Small-business budgets.</span>
            </h1>
            <p className="text-xl text-gray-300 mb-2">
              We help small businesses put modern websites, AI, automation, and custom software to work—making powerful tools accessible without the traditional cost or complexity.
            </p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-xl text-neonGreen font-medium italic mb-8"
            >
              Technology once out of reach. Now built around you.
            </motion.p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link to="/contact" className="inline-flex items-center px-8 py-3 rounded-full bg-gradient-to-r from-neonGreen to-electricBlue text-darkGrey font-semibold text-lg shadow-lg shadow-electricBlue/20">
                Let’s talk
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Feature Cards */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Win more customers Card */}
          <FeatureCard
            title="Win more customers"
            icon={<Brain size={40} className="text-neonGreen" />}
            description="Websites that help people discover your business and make it easy to call, book, or request a quote."
          />

          {/* Save time every day Card */}
          <FeatureCard
            title="Save time every day"
            icon={<Cpu size={40} className="text-electricBlue" />}
            description="Automate follow-ups, reminders, and routine admin so you can spend more time on the work that matters."
          />

          {/* Make work easier Card */}
          <FeatureCard
            title="Make work easier"
            icon={<Zap size={40} className="text-deepPurple" />}
            description="Connect your tools and build practical software around the way your business actually runs."
          />
        </div>
      </section>
      <section className="px-4 sm:px-6 lg:px-8 pb-20 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-10 rounded-2xl border border-electricBlue/30 bg-darkPurple p-8 md:p-12">
          <div>
            <p className="text-neonGreen font-semibold mb-3">Start with one useful change</p>
            <h2 className="text-3xl font-bold mb-4">Built around your business.</h2>
            <p className="text-gray-300">From independent shops and local services to online businesses and small teams, we start with what you need. Together, we find a practical first step that fits your priorities and budget.</p>
          </div>
          <ol className="space-y-5 text-gray-300">
            <li><strong className="text-white">1. Tell us what’s getting in the way.</strong><br />No technical brief needed. Start with the problem or idea.</li>
            <li><strong className="text-white">2. Find the right-sized solution.</strong><br />We work through the options, scope, and costs together.</li>
            <li><strong className="text-white">3. Put it to work.</strong><br />Build something useful, learn what works, and grow from there.</li>
          </ol>
        </div>
      </section>
      <section className="border-t border-deepPurple/30 px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-7xl mx-auto">
          <p className="text-electricBlue font-semibold mb-3">The Lab</p>
          <h2 className="text-3xl font-bold mb-4">Always exploring what’s next.</h2>
          <p className="text-gray-300 max-w-2xl mb-6">The Lab is where we experiment with AI and emerging technology, follow our curiosity, and share what we learn. It’s the passion project behind Imagination Labs.</p>
          <Link to="/research" className="inline-flex items-center text-neonGreen hover:underline">Explore the Lab <ArrowRight className="ml-2" size={18} /></Link>
        </div>
      </section>
    </div>
  );
};

// Feature Card Component
const FeatureCard = ({ title, icon, description }) => {
  return (
    <motion.div
      whileHover={{ y: -10 }}
      className="bg-gradient-to-br from-darkGrey to-darkPurple p-6 rounded-2xl border border-deepPurple/30 shadow-xl relative overflow-hidden group"
    >
      {/* Glowing effect on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-electricBlue/5 to-deepPurple/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

      <div className="mb-4 relative z-10">{icon}</div>
      <h3 className="text-2xl font-bold mb-3 text-white relative z-10">{title}</h3>
      <p className="text-gray-300 mb-6 relative z-10">{description}</p>
      <Link to="/solutions"
        className="px-6 py-2 rounded-full bg-gradient-to-r from-electricBlue/20 to-deepPurple/20 border border-electricBlue/50 text-white font-medium relative z-10"
      >
        Explore solutions
      </Link>
    </motion.div>
  );
};

export default Home;
